// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback } from 'react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState } from '../../components/workflow/StatePills'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

const LEVELS = ['', 'debug', 'info', 'warn', 'error']
const LEVEL_COLOR: Record<string, string> = { debug: '#64748b', info: '#0369a1', warn: '#b45309', error: '#b91c1c' }

// The time as the table prints it (UTC), so the From/To funnel compares the day that is on screen.
const utcStamp = (ts: string) => {
  const d = new Date(ts)
  return isNaN(d.getTime()) ? null : d.toISOString().replace('T', ' ').slice(0, 19)
}

// D-UI-2: what each column shows, for its header funnel.
const LOG_FUNNELS: FunnelColumn<api.SystemLogEntry>[] = [
  { key: 'time',     label: 'Time (UTC)', value: e => utcStamp(e.ts) },
  { key: 'level',    label: 'Level',      value: e => e.level },
  { key: 'category', label: 'Category',   value: e => e.category },
  { key: 'code',     label: 'Code',       value: e => e.code },
  { key: 'entity',   label: 'Entity',     value: e => e.entity_type ? `${e.entity_type}:${(e.entity_id ?? '').slice(0, 8)}` : null },
  { key: 'detail',   label: 'Detail',     value: e => e.detail ? Object.entries(e.detail).map(([k, v]) => `${k}=${v}`).join(' ') : null, kind: 'text' },
]

export default function SystemLogsPage() {
  const [date, setDate] = useState('')
  const [level, setLevel] = useState('')
  const [category, setCategory] = useState('')
  const [q, setQ] = useState('')
  const [events, setEvents] = useState<api.SystemLogEntry[]>([])
  const [dates, setDates] = useState<string[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    api.getSystemLogs({ ...(date ? { date } : {}), ...(level ? { level } : {}), ...(category ? { category } : {}), ...(q ? { q } : {}) })
      .then(r => { setEvents(r.events); setDates(r.available_dates); setTruncated(r.truncated); if (!date) setDate(r.date) })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [date, level, category, q])
  useEffect(() => { load() }, [date, level])

  // The header funnels filter the whole loaded set.
  const fx = useTableFunnels(events, LOG_FUNNELS)

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 14 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>System logs</h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          PHI-free structured events for troubleshooting. Retained 30 days.
        </div>
      </header>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select value={date} onChange={e => setDate(e.target.value)} style={sel}>
          {dates.length === 0 && <option value={date}>{date || 'today'}</option>}
          {dates.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={level} onChange={e => setLevel(e.target.value)} style={sel}>
          {LEVELS.map(l => <option key={l} value={l}>{l === '' ? 'All levels' : l}</option>)}
        </select>
        <input value={category} onChange={e => setCategory(e.target.value)} placeholder="category" style={{ ...sel, width: 130 }} />
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load() }} placeholder="search code / id…" style={{ ...sel, width: 200 }} />
        <button onClick={load} style={btn}>Apply</button>
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      {truncated && <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)', marginBottom: 8 }}>Showing the 500 most recent events — narrow the filters to see more.</div>}

      {!loading && events.length > 0 && fx.chips}
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 8 }}>
        {loading ? <LoadingState label="Loading logs…" /> :
         events.length === 0 ? <EmptyState message="No log events for these filters." /> :
        <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', minWidth: 900 }}>
          <thead>
            <tr>
              <th style={th}>Time (UTC) {fx.funnel('time')}</th><th style={th}>Level {fx.funnel('level')}</th><th style={th}>Category {fx.funnel('category')}</th>
              <th style={th}>Code {fx.funnel('code')}</th><th style={th}>Entity {fx.funnel('entity')}</th><th style={th}>Detail {fx.funnel('detail')}</th>
            </tr>
          </thead>
          <tbody>
            {fx.rows.map((e, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={td}>{new Date(e.ts).toISOString().replace('T', ' ').slice(0, 19)}</td>
                <td style={{ ...td, color: LEVEL_COLOR[e.level] ?? 'inherit', fontWeight: 700 }}>{e.level}</td>
                <td style={td}>{e.category}</td>
                <td style={{ ...td, fontWeight: 600 }}>{e.code}</td>
                <td style={td}>{e.entity_type ? `${e.entity_type}:${(e.entity_id ?? '').slice(0, 8)}` : '—'}</td>
                <td style={td}>{e.detail ? Object.entries(e.detail).map(([k, v]) => `${k}=${v}`).join(' ') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  )
}

const sel: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, background: 'var(--color-bg)', color: 'var(--color-text)' }
const btn: React.CSSProperties = { padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary,#2563eb)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const th: React.CSSProperties = { textAlign: 'left', padding: '7px 9px', color: 'var(--color-text-mute)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }
const td: React.CSSProperties = { padding: '6px 9px', verticalAlign: 'top' }
